import React from 'react';
import type { BrokerBoardBuyer } from '../../api/types';
import { SeverityBar } from './SeverityBar';
import { BUYER_5MIL_HELP } from './brokerViewTokens';
import './BoardBuyerLens.css';

const fmtMoney = (n?: number | null) => (n == null ? '–' : `$${Math.round(n).toLocaleString()}`);
const fmtInt = (n?: number | null) => (n == null ? '–' : Math.round(n).toLocaleString());
const fmtNum = (n?: number | null, digits = 1) => (n == null ? '–' : n.toFixed(digits));
/** MMR retention is a RATIO (avg sold price ÷ adjusted MMR); shown as % of MMR (§0.6). */
const fmtRetention = (n?: number | null) => (n == null ? '–' : `${Math.round(n * 100)}% of MMR`);

const TIER_DESC: Record<number, string> = {
  4: 'Exact: same year / mileage / CR cohort + trim + drivetrain',
  3: 'Cohort: same year / mileage / CR band',
  2: 'Similar: same age + mileage or CR band',
  1: 'Make/model only (outside the year/mileage/CR cohort)',
  0: 'Segment: same body/size segment — a different make/model in the same class (incl. same-parent siblings, e.g. GMC ↔ Chevy)',
};
const TIER_COLOR: Record<number, { bg: string; fg: string }> = {
  4: { bg: '#dcfce7', fg: '#15803d' },
  3: { bg: '#dbeafe', fg: '#1d4ed8' },
  2: { bg: '#fef3c7', fg: '#a16207' },
  1: { bg: '#f1f5f9', fg: '#475569' },
};

/** Copy a buyer's 5mil, mirroring the seller tab's "⧉ Copy #".
 *
 *  Rendered through BrokerSliceView's card-actions slot rather than inside the
 *  header: the card head is itself a <button>, so this cannot live in the header
 *  without nesting one button in another. */
export const BrokerBuyerCopyId: React.FC<{ buyer: BrokerBoardBuyer }> = ({ buyer }) => {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    if (!navigator.clipboard?.writeText) {
      console.error('Clipboard unavailable; 5mil not copied');
      return;
    }
    navigator.clipboard.writeText(buyer.buyer_uid).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch((err) => console.error('Failed to copy 5mil', err));
  };
  return (
    <button
      type="button"
      className="bbl-copyid"
      onClick={copy}
      title={`Copy 5mil ${buyer.buyer_uid}`}
      aria-label={`Copy 5mil ${buyer.buyer_uid}`}
    >
      {copied ? 'Copied!' : '⧉ Copy 5mil'}
    </button>
  );
};

/**
 * The buyer slice card header: identity, reach + per-tier match breakdown, and
 * buy stats, plus the shared urgency bar. The VINs themselves live in the shared
 * `BrokerVinTable` drawer the `BrokerSliceView` shell renders on expand. (Fit is
 * a per-VIN buyer↔VIN signal, so it lives in the VIN table, not the buyer card.)
 */
export const BrokerBuyerCardHeader: React.FC<{ buyer: BrokerBoardBuyer; rank: number }> = ({ buyer, rank }) => {
  const loc = [buyer.state, buyer.buyer_zip_code].filter(Boolean).join(' · ');
  const pc = buyer.priority_counts;
  const pcTotal = pc ? pc.severity_critical + pc.severity_high + pc.severity_medium + pc.severity_low : 0;
  return (
    <>
      <div className="bbl-row-main">
        <span className="bbl-rank">#{rank}</span>
        <span className="bbl-name" title={loc}>{buyer.buyer_name || `Buyer ${buyer.buyer_uid}`}</span>
        {/* The 5mil was only visible when the buyer had no name, which is the
            case a broker least needs it. The seller tab shows its number always. */}
        <span className="bbl-uid" title={BUYER_5MIL_HELP}>· {buyer.buyer_uid}</span>
        <span className="bbl-reach" title="Board VINs this buyer is a top-5 match for">
          {fmtInt(buyer.matching_vins)} VINs
        </span>
        {([
          { tier: 4, label: 'Exact', n: buyer.exact_vins },
          { tier: 3, label: 'Cohort', n: buyer.cohort_vins },
          { tier: 2, label: 'Similar', n: buyer.similar_vins },
          { tier: 1, label: 'M/M', n: buyer.mm_vins },
        ] as const).filter((t) => t.n > 0).map((t) => {
          const c = TIER_COLOR[t.tier] ?? TIER_COLOR[1];
          return (
            <span key={t.tier} className="bbl-tier" style={{ background: c.bg, color: c.fg }} title={TIER_DESC[t.tier]}>
              {fmtInt(t.n)} {t.label}
            </span>
          );
        })}
        {buyer.distance_miles != null && (
          <span className="bbl-distance" title="Distance to the nearest of this buyer's matching VINs (Onsite → auction, Offsite → seller)">
            {fmtInt(buyer.distance_miles)} mi
          </span>
        )}
        {(buyer.lost_bids ?? 0) > 0 && (
          <span
            className="bbl-lost"
            title={`Recently bid but didn't win on ${fmtInt(buyer.lost_bids)} unit${buyer.lost_bids === 1 ? '' : 's'} — a 2nd-place re-engagement target. Pair them with the similar available VINs below.`}
          >
            ⟳ Lost {fmtInt(buyer.lost_bids)}
          </span>
        )}
        {buyer.is_canadian && (
          <span className="bbl-ca" title="Previous Canadian buyer (buyer country = Canada). Cross-border transport / import considerations may apply.">
            🇨🇦 CA
          </span>
        )}
      </div>
      <div className="bbl-row-stats">
        <span><b>{fmtInt(buyer.total_buys)}</b> buys</span>
        <span title="This buyer's historical average purchase price across all their buys — not a quote or estimate for this VIN."><b>{fmtMoney(buyer.avg_sold_price)}</b> avg buy</span>
        <span title="Avg sold price as a % of adjusted MMR (retention). 100% = paid exactly MMR; higher = paid above."><b>{fmtRetention(buyer.avg_sold_adj_mmr_ret)}</b></span>
        <span><b>{fmtInt(buyer.total_bids)}</b> bids</span>
        <span><b>{fmtNum(buyer.avg_cr_grade)}</b> CR</span>
        <span><b>{fmtInt(buyer.avg_mileage)}</b> mi</span>
      </div>
      {pc && <div className="bv-slice-sev"><SeverityBar counts={pc} total={pcTotal} /></div>}
    </>
  );
};
